import { ComponentType } from "react";
import { SvgIconProps } from "@mui/material";
import type { FeatureFlagKey } from "../utils/featureFlags";
import HomeIcon from "@mui/icons-material/Home";
import AddIcon from "@mui/icons-material/Add";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import EventNoteIcon from "@mui/icons-material/EventNote";
import GroupsIcon from "@mui/icons-material/Groups";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import HelpIcon from "@mui/icons-material/Help";
import FolderIcon from "@mui/icons-material/Folder";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { NOVA } from "../routes";

// Define team role types (matching backend values)
export type TeamRole = "Admin" | "Owner" | "Creator";

/** A sidebar item's stable id, used by anything that refers to one (e.g. the Ask Noticeboard catalogue). */
export type SidebarItemKey =
  | "home"
  | "create"
  | "notices"
  | "calendar"
  | "users"
  | "circles"
  | "team"
  | "library"
  | "drive"
  | "nova"
  | "helpCenter"
  | "adCenter"
  | "account";

export interface SidebarNavItem {
  key: SidebarItemKey;
  label: string;
  path: string;
  icon: ComponentType<SvgIconProps>;
  iconSrc?: string;
  requiresApproval: boolean;
  teamRoles: TeamRole[];
  schoolOnly?: boolean;
  churchOnly?: boolean;
  featureFlagKey?: FeatureFlagKey;
  /** Unified worksheets + resources entry; visibility is computed in Sidebar from org type and AI flag. */
  libraryHub?: boolean;
}

export interface SidebarConfig {
  expandedWidth: number;
  minimizedWidth: number;
  navItems: SidebarNavItem[];
}

export const sidebarConfig: SidebarConfig = {
  expandedWidth: 240,
  minimizedWidth: 60,
  navItems: [
    {
      key: "home",
      label: "Home",
      path: "/",
      icon: HomeIcon,
      requiresApproval: false,
      teamRoles: ["Admin", "Owner", "Creator"], // All team roles can access
    },
    {
      key: "create",
      label: "Create",
      path: "/create-notice",
      icon: AddIcon,
      requiresApproval: true,
      teamRoles: ["Admin", "Owner", "Creator"], // All team roles can create
    },
    {
      key: "notices",
      label: "Notices",
      path: "/view-notices",
      icon: NotificationsIcon,
      requiresApproval: true,
      teamRoles: ["Admin", "Owner", "Creator"], // All team roles can view
    },
    {
      key: "calendar",
      label: "Calendar",
      path: "/calendar",
      icon: EventNoteIcon,
      requiresApproval: true,
      teamRoles: ["Admin", "Owner", "Creator"], // All team roles can view
    },
    {
      key: "users",
      label: "Users",
      path: "/user-management",
      icon: PeopleIcon,
      requiresApproval: true,
      teamRoles: ["Admin"], // Only Admins can manage users
      featureFlagKey: "userManagement",
    },
    {
      key: "circles",
      label: "Circles",
      path: "/circles",
      icon: GroupsIcon,
      requiresApproval: true,
      teamRoles: ["Admin", "Owner"], // Only Admin and Owner can manage circles
      featureFlagKey: "circleManagement",
    },
    {
      key: "team",
      label: "Team",
      path: "/team-management",
      icon: AdminPanelSettingsIcon,
      requiresApproval: true,
      teamRoles: ["Admin", "Owner"], // Admins and Owners can manage teams
      featureFlagKey: "teamManagement",
    },
    {
      key: "library",
      label: "Library",
      path: "/library",
      icon: MenuBookIcon,
      requiresApproval: true,
      teamRoles: ["Admin", "Owner", "Creator"],
      libraryHub: true,
    },
    {
      key: "drive",
      label: "Drive",
      path: "/drive",
      icon: FolderIcon,
      requiresApproval: true,
      teamRoles: ["Admin", "Owner", "Creator"], // All team roles can access
    },
    {
      key: "nova",
      label: "Nova",
      path: NOVA,
      icon: AutoAwesomeIcon,
      requiresApproval: true,
      teamRoles: ["Admin", "Owner", "Creator"], // Anyone who can make Drafts
      featureFlagKey: "nova",
    },
    {
      key: "helpCenter",
      label: "Help Center",
      path: "/help/videos",
      icon: HelpIcon,
      requiresApproval: false,
      teamRoles: ["Admin", "Owner", "Creator"], // All can access help
    },
    {
      key: "adCenter",
      label: "Ad Center",
      path: "/ad-center",
      icon: ShoppingCartIcon,
      requiresApproval: true,
      teamRoles: ["Admin"], // Only Admins can access Ad Center
      featureFlagKey: "adCenter",
    },
    {
      key: "account",
      label: "Account",
      path: "/profile",
      icon: PersonIcon,
      requiresApproval: false,
      teamRoles: ["Admin", "Owner", "Creator"], // All can access profile
    },
  ],
};

export function getSidebarItem(key: SidebarItemKey): SidebarNavItem {
  const item = sidebarConfig.navItems.find((i) => i.key === key);
  if (!item) throw new Error(`No sidebar item with key ${key}`);
  return item;
}
